/**
 * LibrarySortSheet - 図書館検索結果のソート設定 BottomSheet
 *
 * ソートキー選択（ラジオボタン風のリスト）と、昇順/降順の明示的な切替を提供する。
 * 「関連度」選択時は昇降切替を非表示にし、Calil の返却順のまま。
 */

import { ArrowDown, ArrowUp, Check } from 'lucide-react'

import { BottomSheet } from '@/components/ui/bottom-sheet'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  DEFAULT_SORT_KEY,
  DEFAULT_SORT_ORDER,
  type LibrarySortKey,
  type LibrarySortOrder,
  useLibrarySortStore,
} from '@/store/useLibrarySortStore'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

type SortOption = {
  key: LibrarySortKey
  label: string
  ascLabel: string
  descLabel: string
}

const SORT_OPTIONS: SortOption[] = [
  { key: 'relevance', label: '関連度', ascLabel: '', descLabel: '' },
  { key: 'pubdate', label: '出版日', ascLabel: '古い順', descLabel: '新しい順' },
  { key: 'title', label: 'タイトル', ascLabel: 'あ→ん', descLabel: 'ん→あ' },
  { key: 'author', label: '著者', ascLabel: 'あ→ん', descLabel: 'ん→あ' },
  { key: 'holdings', label: '所蔵館数', ascLabel: '少ない順', descLabel: '多い順' },
]

export const LibrarySortSheet = ({ open, onOpenChange }: Props) => {
  const sortKey = useLibrarySortStore((s) => s.sortKey)
  const sortOrder = useLibrarySortStore((s) => s.sortOrder)
  const setSort = useLibrarySortStore((s) => s.setSort)
  const reset = useLibrarySortStore((s) => s.reset)

  const currentOption = SORT_OPTIONS.find((o) => o.key === sortKey) ?? SORT_OPTIONS[0]

  const handleSelectKey = (key: LibrarySortKey) => {
    if (key === 'relevance') {
      setSort('relevance', DEFAULT_SORT_ORDER)
      return
    }
    // キー変更時は現在の order を維持
    setSort(key, sortOrder)
  }

  const handleSetOrder = (order: LibrarySortOrder) => {
    if (sortKey === 'relevance') return
    setSort(sortKey, order)
  }

  return (
    <BottomSheet title="並び替え" open={open} onOpenChange={onOpenChange} snapPoints={[0.7]}>
      <div className="flex h-full flex-col gap-3 px-4 pb-4">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            現在: {currentOption.label}
            {sortKey !== 'relevance' && ` (${sortOrder === 'asc' ? currentOption.ascLabel : currentOption.descLabel})`}
          </span>
          {(sortKey !== DEFAULT_SORT_KEY || sortOrder !== DEFAULT_SORT_ORDER) && (
            <button type="button" onClick={reset} className="cursor-pointer text-primary hover:underline">
              リセット
            </button>
          )}
        </div>

        <ul className="min-h-0 flex-1 divide-y divide-border/30 overflow-y-auto rounded border border-border/40">
          {SORT_OPTIONS.map((option) => {
            const isSelected = option.key === sortKey
            return (
              <li key={option.key}>
                <button
                  type="button"
                  onClick={() => handleSelectKey(option.key)}
                  aria-pressed={isSelected}
                  className="flex w-full items-center gap-3 px-3 py-3 text-left hover:bg-accent/30"
                >
                  <div className="flex size-5 shrink-0 items-center justify-center">
                    {isSelected && <Check className="size-4 text-primary" />}
                  </div>
                  <span className="flex-1 text-sm">{option.label}</span>
                </button>
              </li>
            )
          })}
        </ul>

        <div className="flex flex-col gap-2">
          <span className="text-xs text-muted-foreground">並び順</span>
          <div className={cn('grid grid-cols-2 gap-2', sortKey === 'relevance' && 'pointer-events-none opacity-50')}>
            <button
              type="button"
              onClick={() => handleSetOrder('asc')}
              aria-pressed={sortOrder === 'asc'}
              disabled={sortKey === 'relevance'}
              className={cn(
                'flex items-center justify-center gap-1.5 rounded border px-3 py-2 text-sm transition',
                sortOrder === 'asc' && sortKey !== 'relevance'
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border/60 hover:bg-accent/30',
              )}
            >
              <ArrowUp className="size-3.5" />
              昇順{currentOption.ascLabel && ` (${currentOption.ascLabel})`}
            </button>
            <button
              type="button"
              onClick={() => handleSetOrder('desc')}
              aria-pressed={sortOrder === 'desc'}
              disabled={sortKey === 'relevance'}
              className={cn(
                'flex items-center justify-center gap-1.5 rounded border px-3 py-2 text-sm transition',
                sortOrder === 'desc' && sortKey !== 'relevance'
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border/60 hover:bg-accent/30',
              )}
            >
              <ArrowDown className="size-3.5" />
              降順{currentOption.descLabel && ` (${currentOption.descLabel})`}
            </button>
          </div>
        </div>

        <div className="mt-auto flex justify-end">
          <Button type="button" variant="default" size="sm" onClick={() => onOpenChange(false)}>
            閉じる
          </Button>
        </div>
      </div>
    </BottomSheet>
  )
}
